import { useMemo, useState } from 'react';
import { evaluateForm } from '../../lib/agentShared/formSchema';
import type { FormField, FormSchemaV2 } from '../../lib/agentShared/formSchema';
import { Cluster, Input, RangeInput, SelectableOptionChip, Textarea, ToolbarActions } from '../ui';

// Story 2-147wg (leaf-component/registry closure sweep): converges this
// preview's raw controls/layout onto the real DS primitives — the raw
// `<form onSubmit>`/useState STATE MANAGEMENT stays untouched by deliberate
// judgment call, matching every other file this lane has already made this
// exact call for: full RHF/`DynamicFormField` convergence (CLAUDE.md's own
// standing Forms rule: "the dynamic capture surface... feeds the same
// Form/FormField+zod resolver... Epic 2F... until it does, do not add a new
// form") is explicitly story 2-95b's scope, not this leaf-level sweep's —
// this preview answers into local `useState`, not a submitted form, and
// rebuilding it onto RHF here would be exactly the scope-creep this story's
// own Mandatory Migration Rules warn against ("zero ... lifecycle ...
// changes" unless a rare, honestly-stated exception).

// Live form renderer: evaluates visibility, requiredness, note/photo/action
// effects and relation (xor/union/allOf/dependsOn) violations as you answer.
export function FormPreview({
  schema,
  focusedSectionId = null,
}: {
  schema: FormSchemaV2;
  focusedSectionId?: string | null;
}): JSX.Element {
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const evaluation = useMemo(() => evaluateForm(schema, answers), [schema, answers]);

  const setAnswer = (key: string, value: unknown) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  const renderInput = (field: FormField) => {
    const value = answers[field.key];
    switch (field.type) {
      case 'select':
        return (
          <Cluster gap={2}>
            {(field.options ?? []).map((opt) => (
              <SelectableOptionChip
                key={opt.id ?? opt.label}
                option={opt}
                selected={value === opt.label}
                onClick={() => setAnswer(field.key, value === opt.label ? undefined : opt.label)}
              />
            ))}
          </Cluster>
        );
      case 'multiselect': {
        const selected = Array.isArray(value) ? (value as string[]) : [];
        return (
          <Cluster gap={2}>
            {(field.options ?? []).map((opt) => {
              const on = selected.includes(opt.label);
              return (
                <SelectableOptionChip
                  key={opt.id ?? opt.label}
                  option={opt}
                  selected={on}
                  onClick={() =>
                    setAnswer(field.key, on ? selected.filter((s) => s !== opt.label) : [...selected, opt.label])}
                />
              );
            })}
          </Cluster>
        );
      }
      case 'boolean':
        return (
          <Cluster gap={2}>
            {['Yes', 'No'].map((label) => (
              <SelectableOptionChip
                key={label}
                option={{ label }}
                selected={value === (label === 'Yes')}
                onClick={() => setAnswer(field.key, value === (label === 'Yes') ? undefined : label === 'Yes')}
              />
            ))}
          </Cluster>
        );
      case 'number':
        return (
          <Input
            type="number"
            className="w-full"
            min={field.min}
            max={field.max}
            value={(value as number | undefined) ?? ''}
            onChange={(e) => setAnswer(field.key, e.target.value === '' ? undefined : Number(e.target.value))}
          />
        );
      case 'rating': {
        const min = field.min ?? 1;
        const max = field.max ?? 10;
        return (
          <div>
            {/* Story 2-147wg found `Input` a bad fit here (box styling built
                for text controls, fighting a native range-slider's own
                thumb/track chrome); story 2-147xd closes the resulting
                design-language/no-raw-control finding by moving the exact
                same native tag onto `RangeInput` (components/ui/Input.tsx)
                — a genuine, thin, real primitive instead of a forced fit. */}
            <RangeInput
              className="w-full"
              min={min}
              max={max}
              step={field.step ?? 1}
              value={(value as number | undefined) ?? min}
              onChange={(e) => setAnswer(field.key, Number(e.target.value))}
            />
            <Cluster justify="between" className="text-label text-muted-foreground">
              <span>{min}</span>
              <span className="font-bold text-foreground">{(value as number | undefined) ?? '—'}</span>
              <span>{max}</span>
            </Cluster>
          </div>
        );
      }
      case 'textarea':
        return (
          <Textarea
            rows={2}
            value={(value as string | undefined) ?? ''}
            onChange={(e) => setAnswer(field.key, e.target.value || undefined)}
          />
        );
      case 'date':
      case 'datetime':
        return (
          <Input
            type={field.type === 'date' ? 'date' : 'datetime-local'}
            className="w-full"
            value={(value as string | undefined) ?? ''}
            onChange={(e) => setAnswer(field.key, e.target.value || undefined)}
          />
        );
      case 'photo':
      case 'signature':
      case 'gps':
        return (
          <ToolbarActions
            size="sm"
            maxVisible={1}
            actions={[{
              id: `preview-${field.type}-${field.id}`,
              label: value ? `${field.type} attached (clear)` : `Attach ${field.type} (simulated)`,
              variant: value ? 'secondary' : 'ghost',
              onClick: () => setAnswer(field.key, value ? undefined : [`${field.type}-attached`]),
            }]}
          />
        );
      case 'instruction':
        return null;
      default:
        return (
          <Input
            type="text"
            className="w-full"
            placeholder={field.placeholder}
            value={(value as string | undefined) ?? ''}
            onChange={(e) => setAnswer(field.key, e.target.value || undefined)}
          />
        );
    }
  };

  const badge = (text: string, tone: 'warn' | 'info') => (
    <span
      key={text}
      className={`rounded-full px-2 py-0.5 text-caption font-semibold uppercase tracking-wide ${
        tone === 'warn' ? 'bg-amber-500/15 text-amber-600' : 'bg-primary/10 text-primary'
      }`}
    >
      {text}
    </span>
  );

  return (
    <div className="space-y-4" data-agent-target="form-preview">
      {evaluation.violations.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
          <p className="font-semibold uppercase tracking-wide">Relation checks</p>
          {evaluation.violations.map((v, i) => (
            <p key={i} className="mt-1">• {v.message}</p>
          ))}
        </div>
      )}
      {schema.sections.map((section) => {
        const visibleFields = section.fields.filter((f) => evaluation.visible[f.key]);
        if (visibleFields.length === 0) return null;
        if (section.kind === 'table') {
          return (
            <section
              key={section.id}
              id={`template-preview-section-${section.id}`}
              data-preview-section-id={section.id}
              className={`rounded-xl border bg-white/40 p-4 dark:bg-white/5 ${
                focusedSectionId === section.id ? 'border-primary ring-1 ring-primary' : 'border-white/15'
              }`}
            >
              <h4 className="text-sm font-semibold text-foreground">{section.name}</h4>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      {visibleFields.map((f) => (
                        <th key={f.id} className="border-b border-border px-2 pb-1 text-left font-semibold text-muted-foreground">
                          {f.label || f.key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {visibleFields.map((f) => (
                        <td key={f.id} className="px-2 pt-2 align-top">{renderInput(f)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-caption text-muted-foreground">Inspectors can add more rows while filling in.</p>
            </section>
          );
        }
        return (
          <section
            key={section.id}
            id={`template-preview-section-${section.id}`}
            data-preview-section-id={section.id}
            className={`rounded-xl border bg-white/40 p-4 dark:bg-white/5 ${
              focusedSectionId === section.id ? 'border-primary ring-1 ring-primary' : 'border-white/15'
            }`}
          >
            <h4 className="text-sm font-semibold text-foreground">{section.name}</h4>
            {section.description && <p className="text-xs text-muted-foreground">{section.description}</p>}
            <div className="mt-3 space-y-3">
              {visibleFields.map((field) => (
                <div key={field.id} className="space-y-1">
                  <Cluster gap={2}>
                    <label className="text-xs font-semibold text-foreground">{field.label}</label>
                    {evaluation.required[field.key] && badge('required', 'info')}
                    {evaluation.noteRequired[field.key] && badge('note required', 'warn')}
                    {evaluation.photoRequired[field.key] && badge('photo required', 'warn')}
                    {evaluation.actionRequired[field.key] && badge('action required', 'warn')}
                  </Cluster>
                  {field.description && <p className="text-label text-muted-foreground">{field.description}</p>}
                  {renderInput(field)}
                </div>
              ))}
            </div>
          </section>
        );
      })}
      {schema.sections.length === 0 && (
        <p className="text-sm text-muted-foreground">No sections yet — build the form to preview it here.</p>
      )}
    </div>
  );
}
